import React, { useState, useEffect } from "react";
import { Crown, Building2, X, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { startCreditPackCheckout, startBusinessCheckout } from "@/lib/upgrade";

/**
 * Reusable gate modal.
 * tier="credit_pack" (default) — premium sentiment features: shows the 3 Credit Packs to buy.
 * tier="business_team" — B2B features: one CTA to the $29/mo Business Team Plan.
 */
const UpgradeModal = ({ open, onClose, tier = "credit_pack", feature = "This feature" }) => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && tier === "credit_pack") {
      api.get("/subscription/plans").then((r) => setPacks(r.data.credit_packs || [])).catch(() => {});
    }
  }, [open, tier]);

  if (!open) return null;

  const buyPack = async (packId) => {
    setLoading(packId); setError("");
    try {
      const r = await startCreditPackCheckout(packId);
      if (!r.redirected) { onClose?.(); window.location.reload(); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError((d && d.message) || d || "Could not start checkout.");
      setLoading("");
    }
  };

  const goBusiness = async () => {
    setLoading("business"); setError("");
    try {
      const r = await startBusinessCheckout();
      if (!r.redirected) { onClose?.(); window.location.reload(); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError((d && d.message) || d || "Could not start checkout.");
      setLoading("");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="upgrade-modal">
      <div className="surface rounded-[2rem] p-8 max-w-sm w-full text-center relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground" data-testid="upgrade-modal-close"><X className="w-5 h-5" /></button>

        {tier === "business_team" ? (
          <>
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-4">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-serif text-2xl mb-2">Business Team Plan</h3>
            <p className="text-muted-foreground text-sm mb-5">{feature} requires the Business Team Plan — $29/mo unlocks it instantly.</p>
            {error && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-3">{error}</div>}
            <button data-testid="upgrade-modal-cta" onClick={goBusiness} disabled={loading === "business"}
              className="w-full flex items-center justify-center gap-2 rounded-2xl py-3 text-white bg-gradient-to-r from-primary to-secondary hover:opacity-90 disabled:opacity-60">
              {loading === "business" ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <><Sparkles className="w-4 h-4" /> Go Business — $29/mo</>}
            </button>
          </>
        ) : (
          <>
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#c9a24a] to-primary flex items-center justify-center mb-4">
              <Crown className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-serif text-2xl mb-2">Unlock Premium</h3>
            <p className="text-muted-foreground text-sm mb-5">{feature} unlocks permanently with any Credit Pack.</p>
            {error && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-3">{error}</div>}
            <div className="space-y-2">
              {packs.map((p) => (
                <button key={p.id} data-testid={`upgrade-pack-${p.id}`} onClick={() => buyPack(p.id)} disabled={!!loading}
                  className="w-full flex items-center justify-between gap-2 rounded-2xl py-3 px-4 text-white bg-gradient-to-r from-primary to-secondary hover:opacity-90 disabled:opacity-60">
                  <span>{p.credits} Cards</span>
                  <span className="font-serif">{loading === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : `$${p.price}`}</span>
                </button>
              ))}
            </div>
          </>
        )}
        <button onClick={onClose} className="w-full mt-3 text-xs text-muted-foreground hover:text-foreground py-1">Not now</button>
      </div>
    </div>
  );
};

export default UpgradeModal;
